import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import {
  AlertModal,
  Button,
  EmptyState,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchInput,
  Skeleton,
  Table,
  toast,
} from '@keboola/design';
import { Plus, Terminal } from '@keboola/design/icons';

import { useCurrentVendor } from '../../auth/context';
import { QueryError } from '../../components/QueryError';
import { getApiErrorMessage } from '../../features/auth/errors';
import { CreateServiceAccountModal } from '../../features/vendors/components/CreateServiceAccountModal';
import { filterServiceAccounts } from '../../features/vendors/members';
import { useDeleteServiceAccount, useServiceAccounts } from '../../features/vendors/queries';

export const Route = createFileRoute('/~/service-accounts')({
  component: VendorServiceAccountsPage,
  head: () => ({ meta: [{ title: 'Service accounts | Keboola Developer Portal' }] }),
});

function VendorServiceAccountsPage() {
  const vendorId = useCurrentVendor();
  const { data: accounts, isLoading, isError, error, refetch } = useServiceAccounts(vendorId);
  const deleteAccount = useDeleteServiceAccount(vendorId);
  const [search, setSearch] = useState('');
  // Owned here: creating the first account flips the count-keyed branches while the password shows.
  const [createOpen, setCreateOpen] = useState(false);

  const visible = useMemo(() => filterServiceAccounts(accounts ?? [], search), [accounts, search]);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Service accounts"
          description="Credentials for programmatic access to the Developer Portal API."
          action={
            // Hidden while empty: the empty state carries its own call to action.
            accounts !== undefined && accounts.length > 0 ? (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                <Plus />
                Create a service account
              </Button>
            ) : undefined
          }
        />
      </PageLayout.Header>

      {/* Outside every count-keyed branch: an unmount destroys the one-time password in its state. */}
      <CreateServiceAccountModal
        vendorId={vendorId}
        open={createOpen}
        onOpenChange={setCreateOpen}
      />

      {isLoading && <Skeleton className="tw:h-64" />}

      {/* Blocking only without data: a failed refetch keeps the previous `data` and must not hide it.
          `h2`: sits directly under the page header's `h1`. */}
      <QueryError
        isError={isError}
        hasData={accounts !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load the service accounts"
        refreshTitle="Could not refresh the service accounts"
        staleNote="Showing the accounts from the last successful load."
        titleAs="h2"
      />

      {accounts !== undefined && accounts.length === 0 && (
        <EmptyState>
          <EmptyState.Icon>
            <Terminal />
          </EmptyState.Icon>
          <EmptyState.Description>
            Service accounts are used for programmatic access to the Developer Portal.
          </EmptyState.Description>
          <EmptyState.Actions>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Create a service account
            </Button>
          </EmptyState.Actions>
        </EmptyState>
      )}

      {accounts !== undefined && accounts.length > 0 && (
        <>
          <PageToolbar>
            <PageToolbar.Search>
              <SearchInput
                allowClear
                placeholder="Search by name or description"
                value={search}
                onValueChange={setSearch}
              />
            </PageToolbar.Search>
          </PageToolbar>

          {visible.length === 0 ? (
            <EmptyState variant="compact">
              <EmptyState.Description>No account matches “{search}”.</EmptyState.Description>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Account name</Table.Head>
                  <Table.Head>Description</Table.Head>
                  <Table.Head>
                    <span className="tw:sr-only">Actions</span>
                  </Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {visible.map((account) => (
                  <Table.Row key={account.username}>
                    <Table.Cell className="tw:font-mono tw:text-sm">{account.username}</Table.Cell>
                    <Table.Cell className="tw:text-muted-foreground">
                      {account.description ?? '—'}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="tw:flex tw:justify-end">
                        <AlertModal
                          // Caught: `AlertModal` closes on settle, so a rejection would read as a delete.
                          onConfirm={async () => {
                            try {
                              await deleteAccount.mutateAsync(account.username);
                            } catch (error) {
                              toast.error(
                                getApiErrorMessage(error, 'Could not delete the service account.'),
                              );
                              return;
                            }
                            toast.success(`${account.username} was deleted.`);
                          }}
                        >
                          <AlertModal.Trigger asChild>
                            <Button variant="danger" size="small">
                              Delete
                            </Button>
                          </AlertModal.Trigger>
                          <AlertModal.Content
                            variant="danger"
                            title={`Delete ${account.username}?`}
                            confirmLabel="Delete account"
                          >
                            Any credentials you hold for this account stop working immediately.
                            Anything automated with them will start failing.
                          </AlertModal.Content>
                        </AlertModal>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </PageLayout>
  );
}
